import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { Box } from '@mui/material';
import { Button, Form, Tooltip } from 'antd';
import Notification01Icon from 'assets/icons/notification01Icon';
import { useLocation } from 'react-router-dom';
import React, { useMemo, useState } from 'react';
import DataTable from 'components/LynDataTable/DataTable';
import { formatNumber, utcConverter } from '../utils/helperFunc';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import ConfirmModal from 'components/modal/ConfirmModal';
import LeaseRecordViewModal from '../../../lease-v9/lease-record-management-v9/CustomComponents/LeaseRecordViewModal';

const styles = `
  .active .MuiTableCell-root:first-of-type {
    background-color: inherit !important;
  }
`;

const LeaseDetailScheduleTable = ({ leaseControlData = [], screen = 'CREATE', queryV3Loader, isClicked, viewData, hasCCAssigned, hasGlAssigned }) => {
	const status = window.leaseActivationStatus;

	const { state } = useLocation();
	const form = Form.useFormInstance();
	const leaseId = form.getFieldValue('leaseId');
	const leaseType = form.getFieldValue('leaseType');
	const [isActionColumnShowAllow, setIsActionColumnShowAllow] = useState(false);

	const [getJournalEntryData, { data: journalEntryData, isLoading: isJournalEntryDataLoading }] = useSendControlDataMutation();
	const [getActionRequiredData] = useSendControlDataMutation();

	const [rowId, setRowId] = useState(null);
	const [showJournalEntryModal, setShowJournalEntryModal] = useState(false);
	const [tableUpperLeftExtraInfo, setTableUpperLeftExtraInfo] = useState({});
	const [actionRequiredConfirmModal, setActionRequiredConfirmModal] = useState(false);

	React.useEffect(() => {
		const styleElement = document.createElement('style');

		styleElement.innerHTML = styles;
		document.head.appendChild(styleElement);

		return () => {
			document.head.removeChild(styleElement);
		};
	}, []);

	const handleEyeButton = async (row) => {
		const index = row.id;

		setTableUpperLeftExtraInfo(
			screen?.toLowerCase() === 'create'
				? {
						leaseTitle: form.getFieldValue('leaseTitle'),
						leasePeriod: index + 1,
					}
				: {
						leaseId,
						leaseTitle: form.getFieldValue('leaseTitle'),
						leasePeriod: index + 1,
					}
		);
		let calculateData;
		let identityData;
		const formData = form.getFieldsValue();

		if (screen?.toLowerCase() === 'create' || (screen?.toLowerCase() === 'revise' && formData?.modificationSequence === 0)) {
			calculateData = {
				advanceAmount: formData?.advanceAmount,
				leaseTerm: formData?.leaseTerm,
				discountRate: formData?.initialDiscountRate,
				startYear: String(formData?.leaseStartDate?.year()),
				escalation: 0,
				startMonth: String(formData?.leaseStartDate?.month() + 1),
				paymentFrequency: formData?.paymentFrequency,
				paymentTime: formData?.paymentModality,
				advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0)?.length,
				startDay: String(formData?.leaseStartDate?.date()),
				upfrontLeasePayment: String(formData?.upfrontLeasePayment),
				initialDirectCost: String(formData?.initialDirectCost),
				securityDeposit: String(formData?.securityDeposit),
				endOfFinancialYear: formData?.endOfFinancialYear,
				advanceAmounts: formData?.advanceAmounts,
				grossRents: formData?.grossRents,
				changes: [],
				grossRent: 0,
				cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
			};
			identityData = {
				...formData,
				tempLeaseId: formData?.leaseId,
				leaseStartDate: utcConverter(formData?.leaseStartDate),
				leaseTitle: formData?.leaseTitle,
				lessorName: formData?.lessorName,
				leaseTerm: formData?.leaseTerm,
				leaseEndDate: utcConverter(formData?.leaseEndDate),
				paymentModality: formData?.paymentModality,
				leaseType: formData?.leaseType,
				initialDiscountRate: formData?.initialDiscountRate,
				grossRentList: formData?.grossRentList,
				paymentFrequency: formData?.paymentFrequency,
				advanceAmountList: formData?.advanceAmountList,
				advanceAmount: formData?.advanceAmount,
				advanceAmounts: formData?.advanceAmounts,
				upfrontLeasePayment: String(formData?.upfrontLeasePayment),
				initialDirectCost: String(formData?.initialDirectCost),
				securityDeposit: String(formData?.securityDeposit),
				grossRents: formData?.grossRents,
				grossRent: 0,
				escalation: 0,
				cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
				changes: formData?.changes?.map((item) => {
					return {
						...item,
						cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
						inputChangeAdvanceAmounts: formData?.advanceAmounts,
						inputChangeGrossRents: formData?.grossRents,
						inputChangeAdvance: formData?.advanceAmounts?.reduce((acc, curr) => acc + curr, 0),
						initialDirectCost: String(formData?.initialDirectCost ?? 0),
						securityDeposit: String(formData?.securityDeposit ?? 0),
						upfrontLeasePayment: String(formData?.upfrontLeasePayment ?? 0),
					};
				}),
				modifiedDateTime: utcConverter(dayjs()),
				paymentModalityLabel: formData?.paymentModality?.toLowerCase() === 'start' ? 'Advance basis' : 'Acrual Basis',
				endOfFinancialYear: utcConverter(formData?.endOfFinancialYear),
				lastModifiedDate: utcConverter(dayjs()),
				grossRentListSet: formData?.grossRentListSet,
				advanceAmountListSet: formData?.advanceAmountListSet,
				advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0)?.length,
			};

			const payloadData = {
				calculateData,
				identityData,
				scheduledIndex: index + 1,
			};
			const convertJson = JSON.stringify(payloadData);
			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state?.processCode,
				scriptName: 'lease-controller-schedule-findata2',
				payloadData: payloadData,
			};
			try {
				setTimeout(() => {
					setShowJournalEntryModal(true);
				}, 500);
				await getJournalEntryData(formattedPayload);
			} catch (error) {
				console.error('Error in handleEyeButton:', error);
			}
		} else {
			const effectiveChanges = form.getFieldValue('changes')?.filter((_, index) => index !== 0);
			calculateData = {
				advanceAmount:
					formData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce((accumulator, currentValue) => accumulator + currentValue, 0) ?? 0,
				leaseTerm: formData?.changes?.[0]?.inputChangeLeaseTerm,
				discountRate: formData?.changes?.[0]?.inputChangeDiscountRate,
				startDay: String(formData?.leaseStartDate?.date()),
				startMonth: String(formData?.leaseStartDate?.month() + 1),
				startYear: String(formData?.leaseStartDate?.year()),
				escalation: 0,
				paymentFrequency: formData?.changes?.[0]?.paymentFrequency,
				paymentTime: formData?.changes?.[0]?.paymentTime,
				advanceDuration: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
				upfrontLeasePayment: String(formData?.changes?.[0]?.upfrontLeasePayment),
				cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
				initialDirectCost: String(formData?.changes?.[0]?.initialDirectCost),
				securityDeposit: String(formData?.securityDeposit),
				endOfFinancialYear: formData?.endOfFinancialYear,
				advanceAmounts: formData?.changes?.[0]?.inputChangeAdvanceAmounts,
				grossRents: formData?.changes?.[0]?.inputChangeGrossRents,
				changes: effectiveChanges.map((item, index) => {
					if (index === effectiveChanges.length - 1) {
						return {
							...item,
							cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
							initialDirectCost: String(formData?.initialDirectCost ?? 0),
							securityDeposit: String(formData?.securityDeposit ?? 0),
							upfrontLeasePayment: String(formData?.upfrontLeasePayment ?? 0),
						};
					}
					return {
						...item,
					};
				}),
				grossRent: 0,
			};
			const { changes, leaseId, ...rest } = formData;
			// console.log('🚀 ~ changes:changes.map ~ isClicked:', isClicked);
			// console.log('🚀 ~ changes:changes.map ~ status:', status);
			// console.log('🚀 ~ changes:changes.map ~ status2:', ['active', 'closed', 'inactive'].includes(status?.toLowerCase()));

			const leaseIdRefactored =
				!isClicked && ['active', 'closed', 'inactive'].includes(status?.toLowerCase()) ? { leaseId } : { tempLeaseId: leaseId };

			identityData = {
				...rest,
				// ...(!isClicked && ['active', 'closed', 'inactive'].includes(status?.toLowerCase()) && { leaseId }),
				...leaseIdRefactored,
				changes: changes.map((item) => {
					return {
						...item,
						inputChangeAdvance: item?.inputChangeAdvanceAmounts?.reduce((acc, curr) => acc + curr, 0),
						cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
						initialDirectCost: String(formData?.initialDirectCost ?? 0),
						securityDeposit: String(formData?.securityDeposit ?? 0),
						upfrontLeasePayment: String(formData?.upfrontLeasePayment ?? 0),
					};
				}),
			};
			const payloadData = {
				calculateData,
				identityData: {
					...identityData,
					cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
					initialDirectCost: String(formData?.initialDirectCost ?? 0),
					securityDeposit: String(formData?.securityDeposit ?? 0),
					upfrontLeasePayment: String(formData?.upfrontLeasePayment ?? 0),
				},
				scheduledIndex: index + 1,
				calculate: false,
			};
			// console.log(payloadData, 'payloadData2233');
			const convertJson = JSON.stringify(payloadData);
			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state?.processCode,
				scriptName: 'lease-controller-schedule-findata2',
				payloadData: payloadData,
			};
			try {
				setTimeout(() => {
					setShowJournalEntryModal(true);
				}, 500);
				await getJournalEntryData(formattedPayload);
			} catch (error) {
				console.error('Error in handleEyeButton:', error);
			}
		}
	};

	const handleActionRequiredButton = async () => {
		const payloaddata = {
			id: rowId,
		};
		const convertJson = JSON.stringify(payloaddata);
		const convertBase64 = btoa(convertJson);
		const formattedPayload = {
			data: convertBase64,
			processCode: state?.processCode,
			scriptName: 'lease-controller-schedule-ready',
			payloadData: payloaddata,
		};

		try {
			await getActionRequiredData(formattedPayload);
			// Refresh the lease schedule data after action is completed
			if (typeof queryV3Loader === 'function') {
				await queryV3Loader(true);
			}
		} catch (error) {
			console.error('Error in handleActionRequiredButton:', error);
		}
	};

	const columnData = useMemo(() => {
		if (leaseType === 'Operating Lease') {
			return [
				{
					id: 'date',
					width: '5%',
					align: 'center',
					accessor: 'date',
					cellAlign: 'center',
					Header: 'Date',
				},
				{
					id: 'period',
					width: '5%',
					align: 'center',
					accessor: 'period',
					cellAlign: 'center',
					Header: 'Period',
				},
				{
					id: 'grossRent',
					width: '5%',
					align: 'center',
					accessor: 'grossRent',
					cellAlign: 'center',
					Header: 'Gross Pay',
				},
				{
					id: 'advanceAmount',
					width: '5%',
					align: 'center',
					accessor: 'advanceAmount',
					cellAlign: 'center',
					Header: 'Advance',
				},
				{
					id: 'netPay',
					width: '5%',
					align: 'center',
					accessor: 'netPay',
					cellAlign: 'center',
					Header: 'Net Pay',
				},
				{
					id: 'payment',
					width: '5%',
					align: 'right',
					accessor: 'payment',
					cellAlign: 'right',
					Header: 'Payment',
				},
				...(isActionColumnShowAllow
					? [
							{
								Header: 'Action',
								Cell: (cell) => {
									// Only show the Action Required button when needed
									if (cell?.row?.original?.processStatus?.toLowerCase() === 'returned') {
										return (
											<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
												<div style={{ textAlign: 'center' }}>
													<Tooltip title="Action Required">
														<Button
															type="text"
															icon={<Notification01Icon width={16} height={16} color="#B00020" />}
															onClick={(e) => {
																e.stopPropagation();
																setRowId(cell?.row?.original?._id);
																setActionRequiredConfirmModal(true);
															}}
														></Button>
													</Tooltip>
												</div>
											</Box>
										);
									}
									// Return null if no action is needed
									return null;
								},
								disableSortBy: 'true',
								align: 'center',
							},
						]
					: []),
			];
		} else {
			return [
				{
					id: 'date',
					width: '5%',
					align: 'center',
					accessor: 'date',
					cellAlign: 'center',
					Header: 'Date',
				},
				{
					id: 'period',
					width: '5%',
					align: 'center',
					accessor: 'period',
					cellAlign: 'center',
					Header: 'Period',
				},
				{
					id: 'grossRent',
					width: '5%',
					align: 'center',
					accessor: 'grossRent',
					cellAlign: 'center',
					Header: 'Gross Pay',
				},
				{
					id: 'advanceAmount',
					width: '5%',
					align: 'center',
					accessor: 'advanceAmount',
					cellAlign: 'center',
					Header: 'Advance',
				},
				{
					id: 'netPay',
					width: '5%',
					align: 'center',
					accessor: 'netPay',
					cellAlign: 'center',
					Header: 'Net Pay',
				},

				{
					id: 'ibr',
					width: '5%',
					align: 'center',
					accessor: 'ibr',
					cellAlign: 'center',
					Header: 'IBR (%)',
				},

				{
					id: 'discountFactor',
					width: '5%',
					align: 'center',
					accessor: 'discountFactor',
					cellAlign: 'center',
					Header: 'Discount Factor',
				},

				{
					id: 'rentExp',
					width: '5%',
					align: 'center',
					accessor: 'rentExp',
					cellAlign: 'center',
					Header: 'PV of Rent',
				},

				{
					id: 'openLeaseLiability',
					width: '5%',
					align: 'right',
					accessor: 'openLeaseLiability',
					cellAlign: 'right',
					Header: 'Open Lease Liability',
				},
				{
					id: 'adjustment',
					width: '5%',
					align: 'right',
					accessor: 'adjustment',
					cellAlign: 'right',
					Header: 'Adjustment',
				},
				{
					id: 'interestExp',
					width: '5%',
					align: 'right',
					accessor: 'interestExp',
					cellAlign: 'right',
					Header: 'Interest Expense',
				},
				{
					id: 'payment',
					width: '5%',
					align: 'right',
					accessor: 'payment',
					cellAlign: 'right',
					Header: 'Payment',
				},

				{
					id: 'closingLeaseLiability',
					width: '5%',
					align: 'right',
					accessor: 'closingLeaseLiability',
					cellAlign: 'right',
					Header: 'Closing Lease Liability',
				},
				{
					id: 'principal',
					width: '5%',
					align: 'right',
					accessor: 'principal',
					cellAlign: 'right',
					Header: 'Principal',
				},
				{
					id: 'cllfye',
					width: '5%',
					align: 'right',
					accessor: 'cllfye',
					cellAlign: 'right',
					Header: 'Current Lease Liability Financial Year End',
				},
				{
					id: 'cllr12',
					width: '5%',
					align: 'right',
					accessor: 'cllr12',
					cellAlign: 'right',
					Header: 'Current Lease Liability (Rolling 12)',
				},
				{
					id: 'ncllfye',
					width: '5%',
					align: 'right',
					accessor: 'ncllfye',
					cellAlign: 'right',
					Header: 'Non-Current Lease Liability Financial Year End',
				},
				{
					id: 'ncllr12',
					width: '5%',
					align: 'right',
					accessor: 'ncllr12',
					cellAlign: 'right',
					Header: 'Non-Current Lease Liability (Rolling 12)',
				},
				...(isActionColumnShowAllow
					? [
							{
								Header: 'Action',
								Cell: (cell) => {
									// Only show the Action Required button when needed
									if (cell?.row?.original?.processStatus?.toLowerCase() === 'returned') {
										return (
											<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
												<div style={{ textAlign: 'center' }}>
													<Tooltip title="Action Required">
														<Button
															type="text"
															icon={<Notification01Icon width={16} height={16} color="#B00020" />}
															onClick={(e) => {
																e.stopPropagation();
																setRowId(cell?.row?.original?._id);
																setActionRequiredConfirmModal(true);
															}}
														></Button>
													</Tooltip>
												</div>
											</Box>
										);
									}
									// Return null if no action is needed
									return null;
								},
								disableSortBy: 'true',
								align: 'center',
							},
						]
					: []),
			];
		}
	}, [isActionColumnShowAllow, leaseType]);

	const tableData = Array.isArray(leaseControlData)
		? leaseControlData?.map((item, index) => ({
				...item,
				date: dayjs(item.date).format('YYYY-MM-DD'),
				id: index,
				period: item?.period,
				grossRent: formatNumber(item.grossRent, 2),
				discountFactor: formatNumber(item.discountFactor, 4),
				rentExp: formatNumber(item.rentExp, 2),
				interestExp: formatNumber(item.interestExp, 2),
				openLeaseLiability: formatNumber(item.openLeaseLiability, 2),
				interest: formatNumber(item.interest, 2),
				payment: formatNumber(item.payment, 2),
				closingLeaseLiability: formatNumber(item.closingLeaseLiability, 2),
				adjustment: formatNumber(item.adjustment, 2),
				advanceAmount: formatNumber(item.advanceAmount, 2),
				netPay: formatNumber(item.netPay, 2),
				ibr: formatNumber(item.ibr, 2),
				principal: formatNumber(item.principal, 2),
				cllfye: formatNumber(item.cllfye, 2),
				cllr12: formatNumber(item.cllr12, 2),
				ncllfye: formatNumber(item.ncllfye, 2),
				ncllr12: formatNumber(item.ncllr12, 2),
			}))
		: [];

	const ModalData = {
		head: 'Returned Lease - Required Action',
	};

	const rowIndexColorMapping = useMemo(() => {
		const statusColorMap = {
			Pending: '#E6F0FF', // light blue
			// Pending: 'red', // light blue
			Unprocessed: '#E6F0FF', // light blue
			Reversed: '#ffcccc', // light red
			Returned: '#FFFDE6', // light yellow
			Recorded: '#E6FFE6', // light green
			Processed: '#E6F0FF', // light blue
		};

		let newMapping = {};

		Array.isArray(leaseControlData) &&
			leaseControlData?.forEach((lease, index) => {
				if (lease.processStatus) {
					if (lease.processStatus.toLowerCase() === 'returned') {
						setIsActionColumnShowAllow(true);
					}

					if (!isEmpty(viewData?.refCCAssignmentIdentityId) && !isEmpty(viewData?.refCCAssignmentIdentityId)) {
						newMapping[index] = {
							rowColor: statusColorMap[lease.processStatus] || '#FCFCFC',
						};
					}
				}
			});

		return newMapping;
	}, [leaseControlData]);

	return (
		<>
			{Array.isArray(tableData) && tableData?.length !== 0 && (
				<DataTable
					pagination={false}
					DATA={tableData || []}
					firstColStrick={false}
					COLUMNS={columnData || []}
					pageSize={tableData.length}
					xlsxFileName={`${leaseId}_date`}
					rowIndexColorMapping={rowIndexColorMapping}
					onClickHandler={(row) => handleEyeButton(row)}
					defaultVisibleColumn={['principal', 'cllfye', 'cllr12', 'ncllfye', 'ncllr12']}
				/>
			)}

			{showJournalEntryModal && (
				<LeaseRecordViewModal
					showModal={showJournalEntryModal}
					setShowModal={setShowJournalEntryModal}
					isDataLoading={isJournalEntryDataLoading}
					tableUpperLeftExtraInfo={tableUpperLeftExtraInfo}
					checkResponseData={journalEntryData?.responseData || []}
				/>
			)}

			{actionRequiredConfirmModal && (
				<ConfirmModal
					modalData={ModalData}
					showModal={actionRequiredConfirmModal}
					handleSubmit={handleActionRequiredButton}
					setShowModal={setActionRequiredConfirmModal}
				/>
			)}
		</>
	);
};
export default LeaseDetailScheduleTable;
